import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getChartData } from '../../services/api';

function PriceChartWidget({ ticker, dateRange, isLinked, crosshairDate, onCrosshairMove }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadChartData();
  }, [ticker, dateRange]);

  const loadChartData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getChartData(ticker, dateRange);
      setChartData(response.data.data || []);
    } catch (err) {
      console.error('Error loading chart data:', err);
      setError('Failed to load chart data');
    } finally {
      setLoading(false);
    }
  };

  const handleMouseMove = (data) => {
    if (!isLinked || !data || !data.activeLabel) return;
    onCrosshairMove(data.activeLabel);
  };

  const handleMouseLeave = () => {
    if (isLinked) {
      onCrosshairMove(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text/60 text-sm">Loading chart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500 text-sm">{error}</div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text/60 text-sm">No data available</div>
      </div>
    );
  }

  const formatDate = (value) => {
    const date = new Date(value);
    return (date.getMonth() + 1) + '/' + date.getDate();
  };

  const formatPrice = (value) => {
    return '$' + value.toFixed(2);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart 
        data={chartData} 
        margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <XAxis
          dataKey="date"
          stroke="#666"
          fontSize={10}
          tickFormatter={formatDate}
        />
        <YAxis
          stroke="#666"
          fontSize={10}
          domain={['auto', 'auto']}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '4px',
            fontSize: '12px'
          }}
          labelStyle={{ color: '#fff' }}
          formatter={(value) => [formatPrice(value), 'Close']}
        />
        <Line
          type="monotone"
          dataKey="close"
          stroke="#3b82f6"
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
        {isLinked && crosshairDate && (
          <ReferenceLine
            x={crosshairDate}
            stroke="rgba(255, 255, 255, 0.5)"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default PriceChartWidget;
